import React, { useState } from 'react';
import {
  Percent,
  Mail,
  Globe,
  HelpCircle,
  ListOrdered,
  Shield,
  Headphones,
  ListChecks,
  User,
  KeyRound,
  Lock,
  Banknote,
} from 'lucide-react';
import CommissionSettings from '../../components/admin/settings/CommissionSettings';
import EmailSettings from '../../components/admin/settings/EmailSettings';
import SiteSettings from '../../components/admin/settings/SiteSettings';
import SettlementSettings from '../../components/admin/settings/SettlementSettings';
import AdminHelpSettings from '../../components/admin/settings/AdminHelpSettings';
import HowItWorksSettings from '../../components/admin/settings/HowItWorksSettings';
import SafetySettings from '../../components/admin/settings/SafetySettings';
import VendorSupportSettings from '../../components/admin/settings/VendorSupportSettings';
import AdminAccountSettings from '../../components/admin/settings/AdminAccountSettings';
import AdminPasswordSettings from '../../components/admin/settings/AdminPasswordSettings';
import TwoFactorAuthSettings from '../../components/shared/TwoFactorAuthSettings';
import { AdminPageHeader, AdminGuideCallout } from '../../components/admin/guidance';
import { ADMIN_PAGE_GUIDES } from '../../utils/adminPageGuides';

const AdminSettings = () => {
  const [activeTab, setActiveTab] = useState('account');

  const tabs = [
    { id: 'account', label: 'Account', icon: User },
    { id: 'password', label: 'Password', icon: Lock },
    { id: 'twofactor', label: 'Two-Factor', icon: KeyRound },
    { id: 'commission', label: 'Commission', icon: Percent },
    { id: 'settlement', label: 'Settlement', icon: Banknote },
    { id: 'email', label: 'Email', icon: Mail },
    { id: 'site', label: 'Site Settings', icon: Globe },
    { id: 'how-it-works', label: 'How It Works', icon: ListOrdered },
    { id: 'safety', label: 'Safety', icon: Shield },
    { id: 'vendor-support', label: 'Vendors Support', icon: Headphones },
    { id: 'help', label: 'Help', icon: HelpCircle },
  ];

  return (
    <div className="space-y-5">
      <AdminPageHeader
        title="Settings"
        subtitle="Your admin account security, plus platform-wide configuration."
      />

      <AdminGuideCallout
        title={ADMIN_PAGE_GUIDES.settings.title}
        steps={ADMIN_PAGE_GUIDES.settings.steps}
        icon={ListChecks}
      />

      <div className="rounded-2xl border border-gray-200/80 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-sm overflow-hidden">
        <div className="border-b border-gray-200 dark:border-gray-800 px-4 py-3 sm:px-5 overflow-x-auto">
          <div
            className="inline-flex flex-wrap rounded-full bg-gray-100 dark:bg-gray-800 p-1 border border-gray-200/80 dark:border-gray-700"
            role="tablist"
            aria-label="Settings sections"
          >
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return (
                <button
                  key={tab.id}
                  type="button"
                  role="tab"
                  aria-selected={isActive}
                  onClick={() => setActiveTab(tab.id)}
                  className={`inline-flex items-center gap-2 px-3.5 py-2 rounded-full text-sm font-medium transition-all whitespace-nowrap ${
                    isActive
                      ? 'bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-50 shadow-sm'
                      : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-200'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        <div className="p-4 sm:p-5">
          {activeTab === 'account' && <AdminAccountSettings />}
          {activeTab === 'password' && <AdminPasswordSettings />}
          {activeTab === 'twofactor' && (
            <TwoFactorAuthSettings queryKey="admin-two-factor-status" />
          )}
          {activeTab === 'commission' && <CommissionSettings />}
          {activeTab === 'settlement' && <SettlementSettings />}
          {activeTab === 'email' && <EmailSettings />}
          {activeTab === 'site' && <SiteSettings />}
          {activeTab === 'how-it-works' && <HowItWorksSettings />}
          {activeTab === 'safety' && <SafetySettings />}
          {activeTab === 'vendor-support' && <VendorSupportSettings />}
          {activeTab === 'help' && <AdminHelpSettings />}
        </div>
      </div>
    </div>
  );
};

export default AdminSettings;
